/*---------------------------------------------------------------------------------------------
 *  Copyright (c)Cleartrip. All rights reserved.
 * 
 *  This Component is Used for rendering the Benefits form and taking in individual Benefit entries from user
*--------------------------------------------------------------------------------------------*/

import { forwardRef, useEffect, useRef, useState } from "react"

import api from "../../../../api"
import { Fields, RenderFormFieldsHOC, SkeletonFormView } from "../../../../components"

export const NewBenefitModal = forwardRef(({
    showModal,
    categoryType, //Rule type selected before opening this Modal
    closeModalCallback,
    formConfigLoading, //loading state for fetching benefit base config
    formConfig, //Form ui config for benefit base
    formSubmitCallback, //callback to fire when the user clicks on Add Benefit button, this would add in the Children of the Rules
    errorCallback
}, modalRef) => {
    const [baseConfig, setBaseConfig] = useState(null)
    //------ States for handling the fetching config of Benifit type ------
    const [benefitTypeConfigLoading, setBenefitTypeLoading] = useState(false)
    const [benefitTypeConfig, setBenefitTypeConfig] = useState(null)
    const [selectedBenefitType, setSelectedBenefitType] = useState(null)
    // -------------------------------------------------------------------
    //------ States for Handling the fetching benefit ui form config by Benefit type and reward type ----
    const [selectedRewardType, setSelectedRewardType] = useState(null)
    const [benefitUIConfigLoading, setBenefitUIConfigLoading] = useState(false)
    const [benefitUIConfig, setBenefitUIConfig] = useState(null)
    const benefitUIConfigRef = useRef(null)
    //--------------------------------------------------------------------------------------------------

    useEffect(() => {
        if (formConfig && !formConfigLoading) {
            //only store the Field Config excluding to section and page parent config
            //storing only the benefit type dropdown config, ignoring the second child as it comes from the API call for fetching by benefit type
            setBaseConfig(formConfig?.children[0]?.children[0]?.children[0])
        }
    }, [formConfig, formConfigLoading])

    //any update in state should refresh data in the ref
    useEffect(() => {
        if (benefitUIConfig) {
            benefitUIConfigRef.current = benefitUIConfig
            setBenefitUIConfigLoading(false)
        }
    }, [benefitUIConfig])

    const getBenefitBaseWithType = async ({
        catalogType,
        catalogId,
        categoryType, //type of rule selected previously
        benefitType
    }) => {
        try {
            setBenefitTypeLoading(true)
            const response = await api.getBenefitBaseWithType({
                catalogId,
                catalogType,
                categoryType,
                benefitType
            })
            //storing only the reward type dropdown config, ignoring the first child of benefit type as it comes from the API call, as we need
            //only 1 dropdown to be shown
            setBenefitTypeConfig(response?.children[0]?.children[0]?.children[1])
            setBenefitTypeLoading(false)
        } catch (err) {
            setBenefitTypeLoading(false)
            console.log("getBenefitBaseWithType err: ", err)
        }
    }

    const getBenefitBaseWithBenfitRewardType = async ({
        catalogType,
        catalogId,
        categoryType,
        benefitType, //type of rule selected previously
        rewardType
    }) => {
        try {
            setBenefitUIConfigLoading(true)
            const response = await api.getBenefitBaseWithBenfitRewardType({
                catalogId,
                catalogType,
                benefitType,
                categoryType,
                rewardType
            })
            setBenefitUIConfig(response)
            setBenefitUIConfigLoading(false)
            benefitUIConfigRef.current = response
        } catch (err) {
            setBenefitUIConfigLoading(false)
            console.log("getBenefitBaseWithBenfitRewardType err: ", err)
        }
    }

    return (
        <dialog ref={modalRef} id="new_benefit_modal" className="modal" open={showModal}>
            <div className="modal-box w-11/12 max-w-5xl h-4/5 flex flex-col gap-2">
                {
                    formConfigLoading &&
                    <div className="skeleton w-72 h-14 rounded-md bg-accent"></div>
                }

                {
                    !formConfigLoading && baseConfig && !benefitUIConfig &&
                    <>
                        {/* This only renders one dropdown to chose the Benefit type,  */}

                        <Fields
                            {...baseConfig}//spread the props for rendering
                            onChangeCallback={(updatedValue) => {
                                const { valueConfig: { selectedValues = [] } = {} } = updatedValue || {}
                                setBaseConfig(updatedValue)
                                if (selectedValues && selectedValues.length > 0) {
                                    getBenefitBaseWithType({
                                        catalogId: "BENEFIT_BASE",
                                        catalogType: "BENEFIT",
                                        categoryType,
                                        benefitType: selectedValues[0]
                                    })
                                    setSelectedBenefitType(selectedValues[0])
                                }
                            }}
                            onErrorCallback={(error) => errorCallback(error)}
                            sectionId={""} //used only for styling in dropdown, to get respective section clientHeight
                            formSubmitClick={false}
                            viewMode={false}
                        />
                    </>
                }

                {
                    benefitTypeConfigLoading &&
                    <div className="skeleton w-72 h-14 rounded-md bg-accent"></div>
                }

                {
                    benefitTypeConfig && !benefitTypeConfigLoading && !benefitUIConfig &&
                    <>
                        <Fields
                            {...benefitTypeConfig}
                            onChangeCallback={(updatedValue) => {
                                const { valueConfig: { selectedValues } } = updatedValue || {}
                                console.log("selectedValues reward type: ", selectedValues)
                                getBenefitBaseWithBenfitRewardType({
                                    catalogId: "BENEFIT_BASE",
                                    catalogType: "BENEFIT",
                                    categoryType,
                                    benefitType: selectedBenefitType,
                                    rewardType: selectedValues[0]
                                })
                                setSelectedRewardType(selectedValues[0])
                            }}
                            onErrorCallback={(error) => errorCallback(error)}
                            sectionId={""} //used only for styling in dropdown, to get respective section clientHeight
                            formSubmitClick={false}
                            viewMode={false}
                        />

                    </>
                }

                {
                    benefitUIConfigLoading &&
                    <SkeletonFormView />
                }

                {
                    !benefitUIConfigLoading && benefitUIConfig && benefitUIConfigRef.current &&
                    <>
                        <RenderFormFieldsHOC
                            config={benefitUIConfigRef.current}
                            parent={benefitUIConfigRef.current}
                            childIndex={0}
                            sectionId={""} //initially the Json has a PAGE as parent
                            formSubmitClick={false}
                            errorCallback={(error) => {
                                errorCallback(error)
                            }}
                            rerenderFormUI={setBenefitUIConfig}
                            viewMode={false}
                        />
                    </>
                }

                <div className="modal-action">
                    <form method="dialog" onSubmit={(evt) => {
                        evt?.stopPropagation()
                        console.log("form invoked")
                        closeModalCallback(benefitUIConfigRef.current?.children[0])
                    }}  >
                        {/* if there is a button in form, it will close the modal */}
                        <button className="btn">Add Benefit</button>
                    </form>
                    <button className="btn" onClick={(evt) => {
                        evt?.stopPropagation()
                        closeModalCallback()
                    }}>Cancel</button>
                </div>
            </div>
        </dialog>
    )
}) 